import { FormGroup, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Validator to check if a field contains only whitespace.
 * @returns A validator function that returns an error if the value is only whitespace
 */
export function noWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null; // Don't validate empty values, use required validator for that
        }

        const isWhitespace = (control.value || '').trim().length === 0;
        return isWhitespace ? { whitespace: true } : null;
    };
}

/**
 * Get the error message for a specific form field.
 * @param form The FormGroup containing the form controls
 * @param fieldName The name of the form control to check
 * @param validationMessages An object mapping validation error keys to messages or functions that return messages
 * @param isSubmitted Whether the form has been submitted
 * @returns The error message for the specified form field, or null if there is no error
 */
export function getFieldError(
    form: FormGroup,
    fieldName: string,
    validationMessages: Record<string, any>,
    isSubmitted: boolean
): string | null {
    const field = form.get(fieldName);
    if (field && field.errors && (field.touched || isSubmitted)) {
        const messages = validationMessages[fieldName];
        for (const errorKey in field.errors) {
            if (messages && messages[errorKey]) {
                const msg = messages[errorKey];

                // If the message is a function, call it with the error value
                if (typeof msg === 'function') {
                    return msg(field.errors[errorKey]);
                }
                return msg;
            }
        }
    }
    return null;
}

/**
 * Check if a form field is invalid.
 * @param form The FormGroup containing the field
 * @param fieldName The name of the form control to check
 * @param isSubmitted Whether the form has been submitted
 * @returns True if the field is invalid, false otherwise
 */
export function isFieldInvalid(form: FormGroup, fieldName: string, isSubmitted: boolean): boolean {
    const field = form.get(fieldName);

    return !!(field && field.invalid && (field.touched || isSubmitted));
}
